(() => {
  if (customElements.get('ap-product-mist')) return;
  const smooth = (a, b, value) => {
    const t = Math.max(0, Math.min(1, (value - a) / (b - a)));
    return t * t * (3 - 2 * t);
  };
  const hash = (x, y) => {
    const value = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
    return value - Math.floor(value);
  };
  const densityNoise = (x, y) => {
    const ix = Math.floor(x), iy = Math.floor(y);
    const u = smooth(0, 1, x - ix), v = smooth(0, 1, y - iy);
    return hash(ix, iy) * (1 - u) * (1 - v) + hash(ix + 1, iy) * u * (1 - v)
      + hash(ix, iy + 1) * (1 - u) * v + hash(ix + 1, iy + 1) * u * v;
  };
  const defaultColor = '#eb82a8';
  const referenceColor = [235, 130, 168];
  const normalizeColor = value => {
    const color = (value || '').trim().toLowerCase();
    if (/^#[0-9a-f]{6}$/.test(color)) return color;
    if (/^#[0-9a-f]{3}$/.test(color)) return '#' + [...color.slice(1)].map(channel => channel + channel).join('');
    return defaultColor;
  };
  const rgb = color => [1, 3, 5].map(index => parseInt(color.slice(index, index + 2), 16));
  const recolor = (shade, base) => {
    if (base.every((channel, index) => channel === referenceColor[index])) return shade;
    const lightness = color => color[0] * 0.2126 + color[1] * 0.7152 + color[2] * 0.0722;
    const reference = lightness(referenceColor);
    const light = lightness(shade);
    return base.map(channel => Math.round(light <= reference
      ? channel * light / reference
      : channel + (255 - channel) * (light - reference) / (255 - reference)));
  };
  const dropletCache = new Map();
  function dropletSprites(color) {
    if (dropletCache.has(color)) return dropletCache.get(color);
    const base = rgb(color);
    const sprites = ['#d78ba8', '#dfa6ba', '#ecc7d5', '#f7e8ef'].map(shade => {
      const tint = '#' + recolor(rgb(shade), base).map(channel => channel.toString(16).padStart(2, '0')).join('');
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 24;
      const ctx = canvas.getContext('2d');
      const glow = ctx.createRadialGradient(12, 12, 0, 12, 12, 12);
      glow.addColorStop(0, tint);
      glow.addColorStop(0.3, tint + '80');
      glow.addColorStop(1, tint + '00');
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, 24, 24);
      return canvas;
    });
    if (dropletCache.size >= 8) dropletCache.delete(dropletCache.keys().next().value);
    dropletCache.set(color, sprites);
    return sprites;
  }
  const cloudCache = new Map();
  function cloudSprites(color) {
    if (cloudCache.has(color)) return cloudCache.get(color);
    const base = rgb(color);
    const cloudTextures = Array.from({ length: 8 }, (_, seed) => {
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = 160;
      const ctx = canvas.getContext('2d');
      const image = ctx.createImageData(160, 160);
      for (let y = 0; y < 160; y++) {
        for (let x = 0; x < 160; x++) {
          const u = x / 159, v = y / 159;
          const radius = Math.hypot((u - 0.5) * 2, (v - 0.5) * 2);
          const angle = Math.atan2(v - 0.5, u - 0.5) + (1 - Math.min(1, radius)) * 2.8;
          const curledU = 0.5 + Math.cos(angle) * radius * 0.5;
          const curledV = 0.5 + Math.sin(angle) * radius * 0.5;
          const warp = densityNoise(u * 3 + seed * 13, v * 3 + seed * 7);
          let field = 0, amplitude = 0.55, scale = 3;
          for (let octave = 0; octave < 3; octave++) {
            field += amplitude * densityNoise(curledU * scale + warp * 1.6 + seed * 19, curledV * scale + seed * 11);
            amplitude *= 0.48;
            scale *= 2.07;
          }
          const edge = 1 - smooth(0.35, 0.98, radius + (warp - 0.5) * 0.18);
          const vaporDensity = densityNoise(u * 3.5 + 4, v * 4.5 + 7) * 0.75
            + densityNoise(u * 7 + 11, v * 8 + 3) * 0.25;
          const density = smooth(0.02, 0.75, field) * edge
            * (0.30 + 1.4 * smooth(0.24, 0.76, vaporDensity))
            * (1 - smooth(0.45, 0.95, radius) * 0.45);
          const light = smooth(0.28, 0.7, field);
          const white = seed % 4 === 0 ? 0.8 + light * 0.2 : 0.08 + light * 0.52;
          const index = (y * 160 + x) * 4;
          image.data[index] = base[0] + (255 - base[0]) * white;
          image.data[index + 1] = base[1] + (255 - base[1]) * white;
          image.data[index + 2] = base[2] + (255 - base[2]) * white;
          image.data[index + 3] = Math.round(density * 245);
        }
      }
      ctx.putImageData(image, 0, 0);
      return canvas;
    });
    if (cloudCache.size >= 8) cloudCache.delete(cloudCache.keys().next().value);
    cloudCache.set(color, cloudTextures);
    return cloudTextures;
  }
  class ProductMist extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      if (this.motion.matches) return;
      this.canvases = [...this.querySelectorAll('canvas')];
      this.contexts = this.canvases.map(canvas => canvas.getContext('2d'));
      if (this.contexts.some(ctx => !ctx)) return;
      this.fogCanvases = [document.createElement('canvas'), document.createElement('canvas')];
      this.fogContexts = this.fogCanvases.map(canvas => canvas.getContext('2d'));
      if (this.fogContexts.some(ctx => !ctx)) return;
      this.duration = Math.max(12, Math.min(40, Number(this.dataset.duration) || 19));
      const count = this.parentElement.getBoundingClientRect().width < 500 ? 4200 : 7600;
      this.droplets = Array.from({ length: count }, (_, index) => {
        const gaussian = Math.sqrt(-2 * Math.log(Math.max(0.0001, hash(index, 5)))) * Math.cos(hash(index, 6) * Math.PI * 2);
        const lateral = Math.max(-1, Math.min(1, gaussian * 0.37));
        const distance = 0.2 + hash(index, 8) * 0.46;
        const population = hash(index, 55);
        const density = 0.25 + 0.75 * smooth(0.18, 0.78,
          densityNoise(lateral * 5 + 9, distance * 16 + 3) * 0.75
          + densityNoise(lateral * 12 + 4, distance * 30) * 0.25);
        return {
          birth: Math.pow(hash(index, 2), 1.35) * 0.055,
          life: this.duration * (0.42 + hash(index, 4) * 0.2),
          distance,
          lateral,
          offset: (hash(index, 9) - 0.5) * 1.6,
          phase: hash(index, 18) * Math.PI * 2,
          size: population > 0.97 ? 0.85 + hash(index, 12) * 0.45 : 0.2 + Math.pow(hash(index, 12), 2) * 0.6,
          haze: population < 0.62,
          density,
          front: index % 5 === 0,
          weight: 0.3 + hash(index, 24) * 0.7,
          sprite: index % 4
        };
      });
      this.color = normalizeColor(this.dataset.color);
      const base = rgb(this.color);
      this.coreColors = [[220, 143, 174], [235, 189, 208], [248, 222, 234]].map(shade => recolor(shade, base).join(', '));
      this.textures = dropletSprites(this.color);
      this.cloudTextures = cloudSprites(this.color);
      this.puffs = Array.from({ length: 64 }, (_, index) => ({
        side: index % 2 ? 1 : -1,
        phase: hash(index, 18) * 2.8,
        spread: 0.09 + hash(index, 12) * 0.08,
        offset: (hash(index, 9) - 0.5) * 0.07,
        rotation: (hash(index, 22) - 0.5) * 1.5,
        weight: 0.65 + hash(index, 24) * 0.35,
        life: this.duration * (0.54 + hash(index, 4) * 0.12) * 1.12,
        sprite: index % 8,
        microdrops: Array.from({ length: 48 }, (_, drop) => {
          const seed = index * 48 + drop;
          const haze = hash(seed, 83) < 0.6;
          return {
            u: 0.1 + hash(seed, 71) * 0.8,
            v: 0.1 + hash(seed, 73) * 0.8,
            haze,
            radius: haze ? 1.2 + hash(seed, 79) * 2.6 : 0.2 + hash(seed, 79) * 0.45
          };
        })
      }));
      this.elapsed = 0;
      this.updateSize = () => {
        const photo = this.parentElement.getBoundingClientRect();
        // la nebbia sale sopra la foto solo fino all'inizio della sezione prodotto:
        // mai sopra il menu o la barra annunci, qualunque cosa misuri l'intestazione
        const section = this.closest('.ap-pdp') || this.parentElement;
        const limit = section.getBoundingClientRect().top;
        const gap = Math.max(0, Math.min(160, photo.top - limit));
        this.style.setProperty('--ap-nebbia-top', `${-gap}px`);
        const rect = this.getBoundingClientRect();
        this.width = rect.width;
        this.height = rect.height;
        const dpr = Math.min(devicePixelRatio || 1, 1.5);
        this.canvases.forEach((canvas, index) => {
          canvas.width = Math.max(1, Math.round(rect.width * dpr));
          canvas.height = Math.max(1, Math.round(rect.height * dpr));
          this.contexts[index].setTransform(dpr, 0, 0, dpr, 0, 0);
        });
        this.fogCanvases.forEach(canvas => {
          canvas.width = Math.max(1, Math.round(rect.width));
          canvas.height = Math.max(1, Math.round(rect.height));
        });
      };
      this.resize = new ResizeObserver(this.updateSize);
      this.resize.observe(this.parentElement);
      this.updateSize();
      this.tick = now => {
        if (this.finished) return;
        if (this.last) this.elapsed += Math.min((now - this.last) / 1000, 0.1);
        this.last = now;
        this.paint(this.elapsed / this.duration);
        if (this.elapsed >= this.duration) this.finish();
        else this.frame = requestAnimationFrame(this.tick);
      };
      this.visibility = () => {
        cancelAnimationFrame(this.frame);
        this.last = 0;
        if (this.running && !document.hidden && !this.finished) this.frame = requestAnimationFrame(this.tick);
      };
      this.reduceMotion = () => { if (this.motion.matches) this.finish(); };
      document.addEventListener('visibilitychange', this.visibility);
      this.motion.addEventListener('change', this.reduceMotion);
      this.image = this.parentElement.querySelector('img');
      this.begin = () => {
        if (!this.isConnected || this.finished || this.running) return;
        this.running = true;
        this.updateSize();
        this.visibility();
      };
      this.imageReady = !this.image || this.image.complete;
      this.schedule = () => {
        if (document.readyState !== 'complete' || !this.imageReady || this.finished || this.running || this.startTimer !== undefined) return;
        this.startTimer = setTimeout(this.begin, 900);
      };
      this.imageLoaded = () => { this.imageReady = true; this.schedule(); };
      window.addEventListener('load', this.schedule, { once: true });
      if (!this.imageReady) {
        this.image.addEventListener('load', this.imageLoaded, { once: true });
        this.image.addEventListener('error', this.imageLoaded, { once: true });
      }
      this.schedule();
    }
    paint(time) {
      const w = this.width, h = this.height;
      this.contexts.forEach(ctx => ctx.clearRect(0, 0, w, h));
      this.fogContexts.forEach(ctx => ctx.clearRect(0, 0, w, h));
      const seconds = time * this.duration;
      const cloudSeconds = seconds + Math.max(0, seconds - 1.3) * 0.1;
      const pulseSeconds = seconds * (1.05 / 0.9);
      const cloudBlend = smooth(0.5, 1.2, seconds);
      const cloudFormation = Math.max(smooth(0.11, 1.3, seconds), smooth(0.11, 0.525, seconds) * 0.45);
      const settling = smooth(3.5, 7, cloudSeconds);
      const finalCurl = smooth(5.5, 9, cloudSeconds);
      const finalDrop = settling * 60;
      const sourceY = h * 0.018;
      const areaHeight = Math.max(1, h - 40);
      const trailingEdge = sourceY + areaHeight * (0.10 + smooth(0.22, 1.3, seconds) * 0.105
        + smooth(1.3, 7, cloudSeconds) * 0.085);
      const scale = Math.min(1, w / 600);
      const back = this.contexts[0];
      if (pulseSeconds > 0 && pulseSeconds < 1.05) {
        const head = areaHeight * 0.62 * (1 - Math.exp(-pulseSeconds * 3));
        const tail = areaHeight * 0.24 * (1 - Math.exp(-Math.max(0, seconds - 0.055) * 3.3));
        const envelope = smooth(0, 0.07, pulseSeconds) * (1 - smooth(0.65, 1.05, pulseSeconds));
        const halfWidth = Math.min(w * 0.27, head * 0.35);
        const core = back.createLinearGradient(w / 2, sourceY + tail, w / 2, sourceY + head);
        core.addColorStop(0, `rgba(${this.coreColors[0]}, 0)`);
        core.addColorStop(0.13, `rgba(${this.coreColors[0]}, 0.12)`);
        core.addColorStop(0.52, `rgba(${this.coreColors[1]}, 0.08)`);
        core.addColorStop(1, `rgba(${this.coreColors[2]}, 0)`);
        back.globalAlpha = envelope / 8;
        back.fillStyle = core;
        for (let band = 0; band < 8; band++) {
          const width = 0.2 + band * 0.1;
          back.beginPath();
          back.moveTo(w / 2 - tail * 0.06 * width, sourceY + tail);
          back.lineTo(w / 2 - halfWidth * width, sourceY + head);
          back.quadraticCurveTo(w / 2, sourceY + head * 1.08, w / 2 + halfWidth * width, sourceY + head);
          back.lineTo(w / 2 + tail * 0.06 * width, sourceY + tail);
          back.closePath();
          back.fill();
        }
      }
      for (const drop of this.droplets) {
        const age = (seconds - drop.birth) * 0.95;
        if (age <= 0 || age >= drop.life) continue;
        const travel = 1 - Math.exp(-age * 3.6);
        const roll = smooth(1.5, 5.5, age);
        const cloud = smooth(0.65, 2.2, age);
        const side = drop.lateral < 0 ? -1 : 1;
        const angle = roll * 4.4 + drop.phase * 0.65;
        const curl = roll * (0.016 + Math.abs(drop.lateral) * 0.03);
        const forward = areaHeight * drop.distance * travel;
        const spread = Math.min(w * 0.3, forward * 0.47) * (1 + cloud * 0.28);
        const x = w / 2 + drop.offset + spread * drop.lateral
          + side * Math.sin(angle) * curl * w
          + Math.sin(drop.phase + age * 1.15) * roll * 0.008 * w;
        const y = sourceY + forward + areaHeight * 0.014 * age
          + (1 - Math.cos(angle)) * curl * areaHeight;
        const fade = smooth(0, 0.045, age) * (1 - smooth(drop.life * 0.46, drop.life, age))
          * (1 - smooth(areaHeight * 0.65, areaHeight * 0.94, y));
        const wrap = drop.front ? smooth(1.7, 3.5, age) * (0.5 + Math.sin(angle) * 0.5) : 0;
        const radius = drop.haze ? (4 + drop.weight * 5 + roll * 3) * scale * (1 + cloud * 0.22) : drop.size * (1.05 + roll * 0.35);
        const initialOpacity = 0.78 * (1 - smooth(0.1, 0.95, Math.abs(drop.lateral)) * 0.72);
        const presence = initialOpacity * (1 - cloud) + (drop.haze ? 1.3 : 1.08) * cloud;
        const alpha = fade * drop.weight * drop.density * (drop.haze ? 0.18 : 0.48) * presence * (1 - cloudBlend);
        const targets = drop.haze ? this.fogContexts : this.contexts;
        targets.forEach((ctx, layer) => {
          const opacity = alpha * (layer === 0 ? 1 - wrap : wrap * 0.65);
          if (opacity <= 0.001) return;
          ctx.globalAlpha = opacity;
          ctx.drawImage(this.textures[drop.sprite], x - radius, y - radius, radius * 2, radius * 2);
        });
      }
      this.contexts.forEach((ctx, layer) => {
        ctx.save();
        ctx.globalAlpha = 1;
        ctx.filter = 'blur(2.5px)';
        ctx.drawImage(this.fogCanvases[layer], 0, 0, w, h);
        ctx.restore();
      });
      // Vapor grows from the advancing spray tip into the established cloud.
      this.fogContexts.forEach(ctx => ctx.clearRect(0, 0, w, h));
      if (cloudFormation > 0) {
        for (const puff of this.puffs) {
          const age = Math.max(0, (cloudSeconds - 0.65) / puff.life);
          if (age >= 1) continue;
          const roll = smooth(0.04, 0.8, age);
          const opening = smooth(0, 0.32, age);
          const angle = settling * 1.3 + puff.phase * 0.3;
          const expansion = 0.18 + cloudFormation * 0.82;
          const x = w * (0.5 + puff.offset * expansion * 0.7 + Math.sin(angle) * settling * 0.008);
          const initialTravelTime = cloudSeconds * (1 + 0.3 * (1 - smooth(0.5, 1.5, cloudSeconds)));
          const cloudY = 0.12 + 0.28 * (1 - Math.exp(-initialTravelTime * 3))
            + smooth(0.65, 5, cloudSeconds) * 0.10
            + puff.offset * 0.7 * smooth(0, 0.3, cloudSeconds);
          const y = sourceY + areaHeight * cloudY;
          const size = 2 * Math.min(w, h * 1.4) * (0.24 + opening * 0.13 + roll * 0.07) * expansion;
          const fade = cloudFormation * (1 - smooth(0.35, 0.9, age))
            * (1 - smooth(0.42, 0.85, age) * 0.35)
            * (1 - smooth(areaHeight * 0.65, areaHeight * 0.94, y + size * 0.075));
          const front = smooth(1.7, 3.5, cloudSeconds) * smooth(0.03, 0.2, age) * (0.5 + 0.5 * Math.sin(angle + puff.phase));
          this.fogContexts.forEach((ctx, layer) => {
            ctx.save();
            ctx.translate(x, y + finalDrop);
            ctx.globalAlpha = fade * puff.weight * 0.38 * (layer === 0 ? (1 - front * 0.65) * 0.075 : front * 0.04);
            // Preserve the rounded vapor texture; the trajectory supplies the downward spread.
            const top = trailingEdge + areaHeight * 0.015 - y;
            const height = Math.max(1, (size * 0.38 - top) * (1 + (1 - settling) * 0.25) * 1.1 - settling * 20);
            const texture = this.cloudTextures[puff.sprite];
            const width = size * 0.62 * 1.2 * 1.08 * (0.78 + settling * 0.22)
              * (1 + settling * 0.22) * (1 - finalCurl * 0.18);
            const slices = 24;
            for (let slice = 0; slice < slices; slice++) {
              const progress = (slice + 0.5) / slices;
              const bend = Math.sin(progress * Math.PI * 1.4 + angle) * w * 0.012 * settling * progress;
              const edgeLift = settling * smooth(0.35, 0.8, progress) * 10
                + finalCurl * (1 - smooth(0.55, 1, progress)) * 30;
              if (edgeLift === 0) {
                ctx.drawImage(texture, 0, texture.height * slice / slices, texture.width, texture.height / slices,
                  bend - width * 0.5, top + height * slice / slices, width, height / slices + 0.25);
              } else {
                const columns = 6;
                const sliceAlpha = ctx.globalAlpha;
                for (let column = 0; column < columns; column++) {
                  const side = ((column + 0.5) / columns - 0.5) * 2;
                  const tipWeight = smooth(0.25, 0.85, Math.abs(side)) * smooth(0.25, 0.65, progress);
                  const curlAngle = finalCurl * Math.PI * 0.72;
                  const curlRadius = Math.min(18, w * 0.03) * tipWeight;
                  const inward = Math.sign(side) * (1 - Math.cos(curlAngle)) * curlRadius;
                  const lift = side * side * edgeLift + Math.sin(curlAngle) * curlRadius;
                  ctx.globalAlpha = sliceAlpha * (1 - finalCurl * tipWeight * 0.3);
                  ctx.drawImage(texture, texture.width * column / columns, texture.height * slice / slices,
                    texture.width / columns, texture.height / slices,
                    bend - width * 0.5 + width * column / columns - inward, top + height * slice / slices - lift,
                    width / columns + 0.25, height / slices + 0.25);
                }
                ctx.globalAlpha = sliceAlpha;
              }
            }
            ctx.restore();
            const fine = this.contexts[layer];
            fine.save();
            for (const drop of puff.microdrops) {
              const edgeDistance = Math.hypot((drop.u - 0.5) * 2, (drop.v - 0.5) * 2);
              const cluster = smooth(0.25, 0.75, densityNoise(drop.u * 3.5 + 4, drop.v * 4.5 + 7));
              fine.globalAlpha = fade * smooth(0.35, 0.8, cloudSeconds) * cluster
                * (1 - smooth(0.4, 0.9, edgeDistance)) * (drop.haze ? 0.20 : 0.45)
                * (layer === 0 ? 1 - front : front * 0.65);
              const dropX = x + (drop.u - 0.5) * width;
              const dropY = y + finalDrop + top + drop.v * height;
              fine.drawImage(this.textures[drop.haze ? 1 : 0], dropX - drop.radius, dropY - drop.radius, drop.radius * 2, drop.radius * 2);
            }
            fine.restore();
          });
        }
        // Release the descending mask only as the final side curls rise.
        this.fogContexts.forEach(ctx => {
          ctx.save();
          ctx.globalAlpha = 1;
          ctx.globalCompositeOperation = 'destination-in';
          const edge = ctx.createLinearGradient(0, trailingEdge + finalDrop, 0, trailingEdge + finalDrop + areaHeight * 0.12);
          edge.addColorStop(0, `rgba(0, 0, 0, ${finalCurl})`);
          edge.addColorStop(1, 'rgba(0, 0, 0, 1)');
          ctx.fillStyle = edge;
          ctx.fillRect(0, 0, w, h);
          ctx.restore();
        });
        this.contexts.forEach((ctx, layer) => {
          ctx.save();
          ctx.globalAlpha = 1;
          ctx.filter = `blur(${8 + settling * 4}px)`;
          ctx.drawImage(this.fogCanvases[layer], 0, 0, w, h);
          ctx.restore();
        });
      }
      this.contexts.forEach(ctx => { ctx.globalAlpha = 1; });
    }

    finish() {
      this.finished = true;
      clearTimeout(this.startTimer);
      cancelAnimationFrame(this.frame);
      this.contexts?.forEach(ctx => ctx.clearRect(0, 0, this.width, this.height));
      this.resize?.disconnect();
      document.removeEventListener('visibilitychange', this.visibility);
      this.motion?.removeEventListener('change', this.reduceMotion);
      window.removeEventListener('load', this.schedule);
      this.image?.removeEventListener('load', this.imageLoaded);
      this.image?.removeEventListener('error', this.imageLoaded);
    }
    disconnectedCallback() { this.finish(); }
  }
  customElements.define('ap-product-mist', ProductMist);
})();
